import type { RouteLocationNormalized } from "vue-router";

const CHUNK_ERROR_PATTERN = /loading chunk|chunkloaderror|failed to fetch dynamically imported module|importing a module script failed/i;
const RELOAD_ATTEMPTS_STORAGE_KEY = "chunk-error:reload-attempts";
const RELOAD_ATTEMPTS_WINDOW_MS = 60_000;
const MAX_RELOAD_ATTEMPTS = 2;

const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

const readSessionEntry = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionEntry = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    return;
  }
};

const readRecentReloadAttempts = () => {
  const stored = readSessionEntry(RELOAD_ATTEMPTS_STORAGE_KEY);
  if (!stored) return 0;
  try {
    const { count, expires } = JSON.parse(stored) as { count?: number; expires?: number };
    return expires && expires > Date.now() ? (count ?? 0) : 0;
  } catch {
    return 0;
  }
};

const rememberReloadAttempt = (attempts: number) =>
  writeSessionEntry(
    RELOAD_ATTEMPTS_STORAGE_KEY,
    JSON.stringify({ count: attempts + 1, expires: Date.now() + RELOAD_ATTEMPTS_WINDOW_MS })
  );

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const toast = useToast();
  const { t } = nuxtApp.$i18n as { t: (key: string) => string };

  const chunkErrorsSinceNavigation = new Set<unknown>();
  router.beforeEach(() => chunkErrorsSinceNavigation.clear());

  nuxtApp.hook("app:chunkError", ({ error }) => {
    console.warn("[chunk-error] chunk indisponible", error);
    chunkErrorsSinceNavigation.add(error);
  });

  const notifyOfflineNavigation = () => {
    // L'`id` stable assure que Nuxt UI dédoublonne les toasts en cascade.
    toast.add({
      id: "offline-navigation",
      title: t("toast.offlineNavigation.title"),
      description: t("toast.offlineNavigation.description"),
      color: "error",
    });
  };

  const offerManualReload = () => {
    toast.add({
      id: "chunk-error-reload",
      title: t("error.chunkError.title"),
      description: t("error.chunkError.message"),
      color: "warning",
      duration: 0,
      actions: [
        {
          label: t("error.actions.reload"),
          color: "secondary",
          onClick: () => {
            window.location.reload();
          },
        },
      ],
    });
  };

  const reloadAtRoute = (to: RouteLocationNormalized) => {
    if (isOffline()) {
      notifyOfflineNavigation();
      return;
    }
    const attempts = readRecentReloadAttempts();
    if (attempts >= MAX_RELOAD_ATTEMPTS) {
      console.warn(`[chunk-error] ${attempts} rechargements en ${RELOAD_ATTEMPTS_WINDOW_MS}ms : bascule en rechargement manuel`);
      offerManualReload();
      return;
    }
    rememberReloadAttempt(attempts);
    reloadNuxtApp({ path: to.fullPath, persistState: true });
  };

  router.onError((error, to) => {
    if (!chunkErrorsSinceNavigation.has(error) && !CHUNK_ERROR_PATTERN.test(error.message)) return;
    reloadAtRoute(to);
  });

  nuxtApp.hook("app:manifest:update", () => {
    router.beforeResolve(reloadAtRoute);
  });
});
